//AvatarAdmin.tsx — avatar.xrscape.ai/admin: each character is an image and the exact prompt Lucy reads

import { useEffect, useState } from 'react'
import { DEFAULT_PROMPT, PROMPT_LIMIT, type AvatarCharacter } from '../../shared/avatarCharacter'
import { customImagePathFor } from '../../shared/customGarment'
import { fetchAvatarCharacters, saveAvatarCharacters, uploadAvatarImage } from './avatarApi'

//A picked-but-unsaved file rides along with the character until Save uploads it.
interface Draft extends AvatarCharacter {
	readonly pendingFile?: File
	readonly previewURL?: string
}

export default function AvatarAdmin() {
	const [characters, setCharacters] = useState<readonly Draft[]>([])
	const [isLoading, setIsLoading] = useState(true)
	const [isSaving, setIsSaving] = useState(false)
	const [message, setMessage] = useState<string | null>(null)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		fetchAvatarCharacters()
			.then(setCharacters)
			.catch((cause: Error) => setError(cause.message))
			.finally(() => setIsLoading(false))
	}, [])

	const update = (id: string, patch: Partial<Draft>) =>
		setCharacters((current) => current.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)))

	const add = () =>
		setCharacters((current) => [
			...current,
			{ id: crypto.randomUUID(), name: '', prompt: DEFAULT_PROMPT, imageId: null },
		])

	const remove = (id: string) => setCharacters((current) => current.filter((entry) => entry.id !== id))

	//List order is carousel order, and the first character is what the mirror starts on.
	const move = (index: number, by: -1 | 1) =>
		setCharacters((current) => {
			const target = index + by
			if (target < 0 || target >= current.length) return current
			const next = [...current]
			next[index] = current[target]
			next[target] = current[index]
			return next
		})

	const pickImage = (id: string, file: File | null) => {
		if (!file) return
		if (!file.type.startsWith('image/')) {
			setError(`"${file.name}" is not an image`)
			return
		}
		update(id, { pendingFile: file, previewURL: URL.createObjectURL(file) })
	}

	const save = async () => {
		setError(null)
		setMessage(null)
		if (characters.some((entry) => !entry.name.trim())) {
			setError('Every character needs a name')
			return
		}
		if (characters.some((entry) => !entry.prompt.trim())) {
			setError('Every character needs a prompt')
			return
		}
		setIsSaving(true)
		try {
			const uploaded = await Promise.all(
				characters.map(async ({ pendingFile, previewURL, ...plain }): Promise<AvatarCharacter> =>
					pendingFile ? { ...plain, imageId: await uploadAvatarImage(pendingFile) } : plain,
				),
			)
			const saved = await saveAvatarCharacters(uploaded)
			setCharacters(saved.characters)
			setMessage(
				`Saved ${saved.characters.length} character${saved.characters.length === 1 ? '' : 's'}. Open mirrors have it now.`,
			)
		} catch (cause) {
			setError((cause as Error).message)
		} finally {
			setIsSaving(false)
		}
	}

	const hasUnsaved = characters.some((entry) => entry.pendingFile)

	return (
		<div className="admin">
			<header className="admin-header">
				<h1>AI Avatar — admin</h1>
			</header>

			<section className="admin-card">
				<p className="row-hint">
					Each character is an image and the exact prompt Lucy reads. Decart's template: "Substitute the
					character in the video with …". Describe the hair, face, skin and clothes in positive words
					("a clean-shaven chin", not "no beard").
				</p>
				{isLoading && <p className="row-hint">Loading…</p>}
				{characters.map((character, index) => (
					<CharacterRow
						key={character.id}
						character={character}
						isFirst={index === 0}
						isLast={index === characters.length - 1}
						onChange={(patch) => update(character.id, patch)}
						onPickImage={(file) => pickImage(character.id, file)}
						onMove={(by) => move(index, by)}
						onRemove={() => remove(character.id)}
					/>
				))}
				<button type="button" className="text-button admin-add" onClick={add}>
					+ Add character
				</button>
			</section>

			{/* Sticky: a long list would otherwise push Save far below the fields. */}
			<div className="admin-actions">
				<span className={error ? 'admin-status is-problem' : message ? 'admin-status' : 'row-hint'} role="status">
					{error ?? message ?? 'Save pushes every change to open mirrors at once, running sessions included.'}
				</span>
				<button type="button" className="admin-save" onClick={() => void save()} disabled={isSaving}>
					{isSaving ? 'Saving…' : hasUnsaved ? 'Save (uploads images)' : 'Save'}
				</button>
			</div>
		</div>
	)
}

function CharacterRow({
	character,
	isFirst,
	isLast,
	onChange,
	onPickImage,
	onMove,
	onRemove,
}: {
	character: Draft
	isFirst: boolean
	isLast: boolean
	onChange: (patch: Partial<Draft>) => void
	onPickImage: (file: File | null) => void
	onMove: (by: -1 | 1) => void
	onRemove: () => void
}) {
	const isOver = character.prompt.length > PROMPT_LIMIT
	return (
		<div className="admin-garment">
			<label className="admin-image">
				{character.previewURL || character.imageId ? (
					<img src={character.previewURL ?? customImagePathFor(character.imageId as string)} alt="" />
				) : (
					<span>Add image</span>
				)}
				<input type="file" accept="image/*" onChange={(event) => onPickImage(event.target.files?.[0] ?? null)} />
			</label>
			<div className="admin-fields">
				<input
					type="text"
					placeholder="Name — shown on the tile"
					value={character.name}
					maxLength={80}
					onChange={(event) => onChange({ name: event.target.value })}
				/>
				<textarea
					placeholder="The exact prompt Lucy reads"
					value={character.prompt}
					rows={5}
					onChange={(event) => onChange({ prompt: event.target.value })}
				/>
				<span className={`row-hint ${isOver ? 'is-problem' : ''}`}>
					{character.prompt.length} / {PROMPT_LIMIT}
					{isOver ? ' — over Decart’s rough limit; Lucy may reject it' : ''}
				</span>
			</div>
			<div className="admin-order">
				<button type="button" className="text-button" disabled={isFirst} onClick={() => onMove(-1)} aria-label="Move up">
					↑
				</button>
				<button type="button" className="text-button" disabled={isLast} onClick={() => onMove(1)} aria-label="Move down">
					↓
				</button>
				<button type="button" className="text-button admin-remove" onClick={onRemove}>
					Remove
				</button>
			</div>
		</div>
	)
}
